import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import { getUsers } from '@/services/ws-companies/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { AsyncSelect } from '@/js/libs/ui/src/V2';
import { useAuth, useImpersonateUser, type UserIdentifiersFragment } from '@/js/libs/use-auth';
import { useLazyRequest } from '@/js/libs/use-request';

import { IMPERSONATED_USER_HEADER_NAME } from 'src/contexts/AuthContext/constants.ts';
import type { Option } from 'src/types.ts';
import { formatUserLabel, getUserOptionValue } from './helpers.ts';

const USER_OPTIONS_PAGE_SIZE = 20;

// Picks the user the logged admin impersonates among the users the search returns as impersonatable: the enabled
// non-admin users of active companies, the ones ws-permissions accepts.
export const UsersSwitch = memo(function UsersSwitch() {
  const [t] = useTranslation(['common']);
  const getUsersRequest = useLazyRequest({
    apiMethod: getUsers,
    // Searches as the logged admin, not as the impersonated user.
    ignoreHeaders: [IMPERSONATED_USER_HEADER_NAME],
  });
  const pending = useSubscribe(getUsersRequest.pending$);
  const { impersonatedUser } = useAuth();
  const impersonateUser = useImpersonateUser();
  const impersonatedUserOption = useMemo(
    () => (impersonatedUser ? { label: formatUserLabel(impersonatedUser), value: impersonatedUser } : null),
    [impersonatedUser],
  );

  const loadOptions = useCallbackRef((filter: string) =>
    getUsersRequest.load({ filter, impersonatable: true, size: USER_OPTIONS_PAGE_SIZE }).then(({ data }) =>
      data.items.map(({ email, firstName, lastName }): Option<UserIdentifiersFragment> => {
        const user = { email, firstName, lastName };
        return { label: formatUserLabel(user), value: user };
      }),
    ),
  );

  // Picking the impersonated user again changes nothing.
  const handleChange = useCallbackRef((option: SingleValue<Option<UserIdentifiersFragment>>) => {
    if (option?.value.email !== impersonatedUser?.email) {
      impersonateUser(option?.value ?? null);
    }
  });

  return (
    <AsyncSelect<Option<UserIdentifiersFragment>>
      isClearable
      onChange={handleChange}
      placeholder={t('common:IMPERSONATE_USER')}
      loadOptions={loadOptions}
      getOptionValue={getUserOptionValue}
      isLoading={pending}
      value={impersonatedUserOption}
    />
  );
});
